import React, { useMemo } from 'react';
import { Area, CartesianGrid, ComposedChart, ReferenceLine, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { OptionsAnalyticsMetricInfoDialog } from './OptionsAnalyticsMetricInfoDialog';
import { formatNative, formatUsd } from './optionsDashboardUtils';
import { formatDateTimeLong, formatSignedUsd, formatSignedNative, getCurrencyDigits, translateCashFlowType } from './optionsPerformanceUtils';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';

const formatAxisDate = (value) => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(value));
};

const getAxisValueFormatter = (selectedCurrency) => {
  if (selectedCurrency === 'usd') {
    return (value) => formatUsd(value, 0);
  }
  return (value) => formatNative(value, selectedCurrency.toUpperCase(), selectedCurrency === 'btc' ? 4 : 4);
};

const getTooltipValueFormatter = (selectedCurrency) => {
  if (selectedCurrency === 'usd') {
    return (value) => formatUsd(value, 2);
  }

  const currencyCode = selectedCurrency.toUpperCase();
  const digits = currencyCode === 'BTC' ? 8 : 4;
  return (value) => formatNative(value, currencyCode, digits);
};

const EquityPointShape = ({ cx, cy }) => {
  if (cx === undefined || cy === undefined) return null;
  return <circle cx={cx} cy={cy} r={5} fill="#22C55E" stroke="#0F172A" strokeWidth={2} />;
};

const MarkerShape = ({ cx, cy, payload }) => {
  if (cx === undefined || cy === undefined) return null;
  const markerType = payload?.type;
  const fill = markerType === 'withdrawal' ? '#F87171' : markerType === 'transfer' ? '#F0B90B' : '#60A5FA';
  return <circle cx={cx} cy={cy} r={5} fill={fill} stroke="#0F172A" strokeWidth={2} />;
};

const PerformanceTooltip = ({ active, payload, valueFormatter, t }) => {
  if (!active || !payload?.length) return null;

  const markerRow = payload.find((entry) => entry?.payload?.isCashFlowMarker)?.payload || null;
  if (markerRow?.markerEvents?.length) {
    return (
      <div className="min-w-[220px] rounded-2xl border border-white/10 bg-[#020617] px-4 py-3 text-sm text-white shadow-2xl" data-testid="options-performance-chart-marker-tooltip">
        {markerRow.markerEvents.map((event) => (
          <div key={`${event.currency}-${event.timestamp_ms}-${event.type}`} className="space-y-1.5" data-testid={`options-performance-chart-marker-tooltip-${event.currency.toLowerCase()}-${event.type}`}>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#BFDBFE]">{translateCashFlowType(event.type, t)}</p>
            <p className="text-base font-bold text-white">{formatSignedNative(event.amount_native, event.currency, getCurrencyDigits(event.currency))}</p>
            <p className="text-sm text-white/70">{formatDateTimeLong(event.timestamp)}</p>
            {event.amount_usd !== null && event.amount_usd !== undefined ? (
              <p className="text-xs text-white/55">≈ {formatSignedUsd(event.amount_usd)}</p>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  const row = payload.find((entry) => entry?.dataKey === 'value' && !entry?.payload?.isCashFlowMarker)?.payload || payload[0]?.payload;
  if (!row) return null;

  return (
    <div className="min-w-[220px] rounded-2xl border border-white/10 bg-[#020617] px-4 py-3 text-sm text-white shadow-2xl" data-testid="options-performance-chart-tooltip">
      <p className="font-semibold text-white" data-testid="options-performance-chart-tooltip-date">
        {formatDateTimeLong(row.snapshot_at)}
      </p>
      <p className="mt-2 text-white/70" data-testid="options-performance-chart-tooltip-label">{t('performance.equityPointLabel')}</p>
      <p className="text-lg font-bold tabular-nums text-white" data-testid="options-performance-chart-tooltip-value">{valueFormatter(row.value)}</p>
      {row.markerEvents?.length ? (
        <div className="mt-3 space-y-1" data-testid="options-performance-chart-tooltip-markers">
          {row.markerEvents.map((event) => (
            <p key={`${event.currency}-${event.timestamp_ms}-${event.type}`} className="text-sm text-[#BFDBFE]">
              {translateCashFlowType(event.type, t)} · {formatSignedNative(event.amount_native, event.currency, getCurrencyDigits(event.currency))}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const buildInfoDialogContent = (title, selectedCurrency, t) => ({
  title,
  description: t('performance.accountEvolutionInfoDescription'),
  sections: [
    {
      heading: t('performance.accountEvolutionInfoSections.whatIsHeading'),
      body: t('performance.accountEvolutionInfoSections.whatIsBody'),
    },
    {
      heading: t('performance.accountEvolutionInfoSections.cashFlowsHeading'),
      body: t('performance.accountEvolutionInfoSections.cashFlowsBody'),
    },
    {
      heading: t('performance.accountEvolutionInfoSections.viewHeading'),
      body: selectedCurrency === 'usd'
        ? t('performance.accountEvolutionInfoSections.usdBody')
        : selectedCurrency === 'btc'
          ? t('performance.accountEvolutionInfoSections.btcBody')
          : t('performance.accountEvolutionInfoSections.ethBody'),
    },
  ],
});

export const OptionsPerformanceEquityChartCard = ({ title, chartModel, selectedCurrency }) => {
  const { t } = useOptionsLocale();
  const axisFormatter = useMemo(() => getAxisValueFormatter(selectedCurrency), [selectedCurrency]);
  const tooltipFormatter = useMemo(() => getTooltipValueFormatter(selectedCurrency), [selectedCurrency]);
  const infoDialog = useMemo(() => buildInfoDialogContent(title, selectedCurrency, t), [selectedCurrency, t, title]);
  const isSinglePoint = chartModel.state === 'single';
  const hasChart = chartModel.state === 'single' || chartModel.state === 'multi';

  return (
    <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-performance-equity-chart-card">
      <CardHeader className="space-y-3 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-2xl font-black tracking-tight sm:text-3xl" data-typography="heading" data-testid="options-performance-equity-chart-title">{title}</CardTitle>
            <div data-testid="options-performance-equity-chart-info-dialog">
              <OptionsAnalyticsMetricInfoDialog info={infoDialog} testId="options-performance-equity-chart-info" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {hasChart ? (
          <div className="space-y-4" data-testid="options-performance-equity-chart-state-has-data">
            <div className="h-[340px] w-full" data-testid="options-performance-equity-chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartModel.series} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id={`options-performance-chart-fill-${selectedCurrency}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22C55E" stopOpacity={0.24} />
                      <stop offset="95%" stopColor="#22C55E" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis
                    dataKey="snapshot_timestamp_ms"
                    type="number"
                    scale="time"
                    domain={chartModel.xDomain}
                    tickFormatter={formatAxisDate}
                    tick={{ fill: 'rgba(255,255,255,0.58)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis domain={chartModel.yDomain} tick={{ fill: 'rgba(255,255,255,0.58)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={axisFormatter} width={96} />
                  <Tooltip content={<PerformanceTooltip valueFormatter={tooltipFormatter} t={t} />} />
                  {chartModel.markerPoints.map((marker) => (
                    <ReferenceLine
                      key={`${marker.currency}-${marker.type}-${marker.timestamp_ms}`}
                      x={marker.snapshot_timestamp_ms}
                      stroke="rgba(96,165,250,0.35)"
                      strokeDasharray="4 4"
                    />
                  ))}
                  {isSinglePoint ? (
                    <Scatter data={chartModel.series} dataKey="value" shape={<EquityPointShape />} isAnimationActive={false} data-testid="options-performance-equity-chart-current-point" />
                  ) : (
                    <Area type="linear" dataKey="value" stroke="#22C55E" strokeWidth={2.5} fill={`url(#options-performance-chart-fill-${selectedCurrency})`} dot={{ r: 2.5, fill: '#22C55E', strokeWidth: 0 }} activeDot={{ r: 4, fill: '#22C55E', strokeWidth: 0 }} isAnimationActive={false} />
                  )}
                  <Scatter data={chartModel.markerPoints} dataKey="value" shape={<MarkerShape />} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {isSinglePoint ? (
              <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3" data-testid="options-performance-equity-chart-single-state-note">
                <p className="text-sm font-semibold text-white">{t('performance.singleSnapshotTitle')}</p>
                <p className="mt-1 text-sm text-white/60">{t('performance.singleSnapshotDescription')}</p>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-white/10 bg-black/20 px-6 py-10 text-center" data-testid="options-performance-equity-chart-empty-state">
            <p className="text-2xl font-black text-white" data-typography="heading" data-testid="options-performance-equity-chart-empty-title">{t('performance.historyUnavailableTitle')}</p>
            <p className="mx-auto mt-3 max-w-2xl text-sm text-white/60" data-testid="options-performance-equity-chart-empty-description">
              {t('performance.historyUnavailableDescription')}
            </p>
          </div>
        )}

        {chartModel.markerLegend.length ? (
          <div className="flex items-center gap-2 rounded-2xl border border-[#60A5FA]/18 bg-[#60A5FA]/8 px-4 py-3 text-sm text-[#D6E8FF]" data-testid="options-performance-cash-flow-legend">
            <span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#60A5FA]" aria-hidden="true" />
            <span className="font-semibold">{t('performance.cashFlowLegend')}</span>
            <span className="text-[#BFDBFE]/72">{chartModel.markerLegend.length}</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default OptionsPerformanceEquityChartCard;